import { X, Download, ChevronUp } from 'lucide-react'
import { Button } from '@shared/components/ui/button'
import { usePWAInstall } from '@shared/hooks/usePWAInstall'

interface PWAInstallDrawerProps {
  isOpen: boolean
  onClose: () => void
  onToggle: () => void
}

export function PWAInstallDrawer({ isOpen, onClose, onToggle }: PWAInstallDrawerProps) {
  const { isInstalling, promptInstall, isInstallable, isInstalled } = usePWAInstall()

  const handleInstallClick = async () => {
    const accepted = await promptInstall()
    if (accepted) {
      onClose()
    }
  }

  // Only show if installable and not installed
  if (!isInstallable || isInstalled) {
    return null
  }

  return (
    <>
      {/* Overlay - clickable to close (only visible when open) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer Container with Handle */}
      <div
        className={`fixed bottom-14 left-0 right-0 z-50 transition-transform duration-300 ease-out ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{
          transform: isOpen ? 'translateY(0)' : 'translateY(calc(100% - 2.25rem))'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-drawer-title"
        id="pwa-install-drawer"
      >
        {/* Handle - positioned at the top, moves with drawer */}
        <div className="mx-auto w-4/5 flex justify-end">
          <button
            onClick={onToggle}
            className="flex flex-col items-center gap-1 px-6 py-2 transition-colors rounded-t-lg bg-accent/90 hover:bg-accent/70 pointer-events-auto"
            aria-label={isOpen ? 'Cerrar instalación' : 'Abrir instalación'}
            aria-expanded={isOpen}
            aria-controls="pwa-install-drawer-content"
          >
            <ChevronUp
              size={20}
              className={`text-accent-foreground/80 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''
                }`}
            />
          </button>
        </div>

        {/* Drawer Content */}
        <div
          id="pwa-install-drawer-content"
          className="mx-auto w-4/5 bg-background border-t border-border rounded-tl-lg shadow-lg p-4 space-y-3"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <Download size={24} className="text-primary flex-shrink-0" />
              <div>
                <h2
                  id="install-drawer-title"
                  className="font-semibold text-foreground"
                >
                  Instala la App
                </h2>
                <p className="text-sm text-muted-foreground">
                  Acceso más rápido desde tu pantalla de inicio
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="px-2 -mt-1"
              aria-label="Cerrar"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Install Button */}
          <Button
            onClick={() => void handleInstallClick()}
            disabled={isInstalling}
            className="w-full bg-primary hover:bg-primary/90"
            size="lg"
          >
            {isInstalling ? 'Instalando...' : 'Instalar Ahora'}
          </Button>
        </div>
      </div>
    </>
  )
}
